import {
  PackageOpen,
  Building2,
  ShoppingBag,
  ShoppingCart,
  type LucideIcon,
} from 'lucide-react';
import { procurementChapters } from './procurementGuide';
import { organizationChapters } from './organizationGuide';
import { portalChapters } from './portalGuide';
import { salesChapters } from './salesGuide';

/** One section within a guide track — renders its own rich content. */
export interface GuideChapter {
  id: string;
  title: string;
  icon: LucideIcon;
  render: () => React.ReactNode;
}

/** A top-level learning track in the Guide hub. */
export interface GuideTrack {
  id: string;
  title: string;
  tagline: string;
  description: string;
  icon: LucideIcon;
  /** Tailwind gradient classes for the hub card accent. */
  accent: string;
  status: 'live' | 'soon';
  /** Reading time estimate, minutes (live tracks). */
  minutes?: number;
  /** Chapters (live tracks). */
  chapters?: GuideChapter[];
  /** Preview bullets (coming-soon tracks). */
  topics?: string[];
}

export const GUIDE_TRACKS: GuideTrack[] = [
  {
    id: 'organization',
    title: 'Set up your organization',
    tagline: 'Get production-ready',
    description:
      'Configure your organization identity, tax & numbering, warehouses, and team & roles so every module and document works correctly from day one.',
    icon: Building2,
    accent: 'from-sky-500/20 to-sky-500/5',
    status: 'live',
    minutes: 8,
    chapters: organizationChapters,
  },
  {
    id: 'procurement',
    title: 'Procurement',
    tagline: 'From request to receipt',
    description:
      'Bring raw materials into the business — request, approve, source from a vendor or the open market, and receive into stock.',
    icon: PackageOpen,
    accent: 'from-primary/20 to-primary/5',
    status: 'live',
    minutes: 9,
    chapters: procurementChapters,
  },
  {
    id: 'portal',
    title: 'Portal integration',
    tagline: 'Connect your storefront',
    description:
      'Expose your catalogue, pricing and delivery options to your own website or app through the Zafroi APIs, keys and webhooks.',
    icon: ShoppingBag,
    accent: 'from-violet-500/20 to-violet-500/5',
    status: 'live',
    minutes: 12,
    chapters: portalChapters,
  },
  {
    id: 'sales',
    title: 'Sales of finished goods',
    tagline: 'Order to delivery',
    description:
      'The full sell-side journey — orders, GST invoicing, dispatch and carrier delivery, returns and refunds.',
    icon: ShoppingCart,
    accent: 'from-emerald-500/20 to-emerald-500/5',
    status: 'live',
    minutes: 11,
    chapters: salesChapters,
  },
];

export function findTrack(id: string | undefined): GuideTrack | undefined {
  return GUIDE_TRACKS.find((t) => t.id === id);
}
